import { useState, useRef } from 'react'
import { router } from '@inertiajs/react'
import html2canvas from 'html2canvas'
import EventLayout from '@/components/layouts/EventLayout'
import { PageHeader } from '@/components/patterns'
import ContentSection from '@/components/shared/ContentSection'
import {
  Button,
  Card,
  Input,
  TextArea,
  Switch,
  ThemeColorPicker,
  ThemeColorButton,
  ImageUpload,
  DEFAULT_THEME_COLORS,
} from '@/components/ui'
import { DatePicker, TimePicker, Select, message, Tooltip } from 'antd'
import {
  CopyOutlined,
  CheckOutlined,
  QrcodeOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  UserAddOutlined,
} from '@ant-design/icons'
import type { UploadFile } from 'antd'
import dayjs from 'dayjs'
import QRCard from '@/components/QRCard'
import QRPreviewModal from '@/components/QRPreviewModal'
import { api } from '@/lib/apiClient'

const { RangePicker } = DatePicker

interface Category {
  id: number
  name: string
}

/** Response of POST /events/:slug/settings/test_registrant */
interface TestRegistrantResult {
  email: string
  otp: string
  created: boolean
  incomplete_answers: boolean
  attendee_app_url: string
}

interface EventSettingsProps {
  event: {
    id: number
    name: string
    slug: string
    status: 'draft' | 'live' | 'archived' | 'purged'
    start_date: string
    end_date: string
    start_time: string | null
    end_time: string | null
    location: string | null
    description: string | null
    theme: string
    client_name: string | null
    allow_public_registration: boolean
    allow_attendee_app_access: boolean
    allow_whatsapp_login: boolean
    auto_approve_registrations: boolean
    show_event_details: boolean
    logo_url: string | null
    logo_filename: string | null
    banner_url: string | null
    banner_filename: string | null
    category_ids: number[]
  }
  categories: Category[]
  preview_urls: {
    attendee_app: string
    registration_form: string
    attendee_app_preview: string
  }
}

export default function EventSettings({ event, categories, preview_urls }: EventSettingsProps) {
  const [formData, setFormData] = useState({
    name: event.name,
    slug: event.slug,
    start_date: event.start_date,
    end_date: event.end_date,
    start_time: event.start_time,
    end_time: event.end_time,
    location: event.location || '',
    description: event.description || '',
    theme: event.theme,
    client_name: event.client_name || '',
    allow_public_registration: event.allow_public_registration,
    allow_attendee_app_access: event.allow_attendee_app_access,
    allow_whatsapp_login: event.allow_whatsapp_login,
    auto_approve_registrations: event.auto_approve_registrations,
    show_event_details: event.show_event_details ?? true,
    category_ids: event.category_ids,
  })

  const [logoFile, setLogoFile] = useState<UploadFile[]>(
    event.logo_url
      ? [{ uid: '-1', name: event.logo_filename || 'logo', status: 'done', url: event.logo_url }]
      : []
  )
  const [bannerFile, setBannerFile] = useState<UploadFile[]>(
    event.banner_url
      ? [{ uid: '-1', name: event.banner_filename || 'banner', status: 'done', url: event.banner_url }]
      : []
  )
  const [removeLogo, setRemoveLogo] = useState(false)
  const [removeBanner, setRemoveBanner] = useState(false)

  const [themeDrawerOpen, setThemeDrawerOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [rotatingPreview, setRotatingPreview] = useState(false)
  const [creatingTestRegistrant, setCreatingTestRegistrant] = useState(false)
  const [testRegistrant, setTestRegistrant] = useState<TestRegistrantResult | null>(null)
  const [revertedMessage, setRevertedMessage] = useState(false)
  const normalizedSlug = formData.slug.trim()
  const slugChanged = normalizedSlug !== event.slug

  // QR Modal state
  const [qrModalOpen, setQrModalOpen] = useState(false)
  const [qrModalType, setQrModalType] = useState<'attendee_app' | 'registration_form'>('attendee_app')

  // Refs for QR cards (for direct download)
  const attendeeAppQrRef = useRef<HTMLDivElement>(null)
  const registrationFormQrRef = useRef<HTMLDivElement>(null)

  // Track when QR cards are ready for download
  const [attendeeAppQrReady, setAttendeeAppQrReady] = useState(false)
  const [registrationFormQrReady, setRegistrationFormQrReady] = useState(false)

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleDateRangeChange = (dates: any) => {
    if (dates) {
      setFormData((prev) => ({
        ...prev,
        start_date: dates[0].format('YYYY-MM-DD'),
        end_date: dates[1].format('YYYY-MM-DD'),
      }))
    }
  }

  const handleStartTimeChange = (time: dayjs.Dayjs | null) => {
    setFormData((prev) => ({
      ...prev,
      start_time: time ? time.format('HH:mm') : null,
      end_time: time ? prev.end_time : null,
    }))
  }

  const handleEndTimeChange = (time: dayjs.Dayjs | null) => {
    setFormData((prev) => ({
      ...prev,
      end_time: time ? time.format('HH:mm') : null,
    }))
  }

  const handleLogoChange = (fileList: UploadFile[]) => {
    setLogoFile(fileList)
    setRemoveLogo(false)
  }

  const handleBannerChange = (fileList: UploadFile[]) => {
    setBannerFile(fileList)
    setRemoveBanner(false)
  }

  const handleLogoRemove = () => {
    setLogoFile([])
    setRemoveLogo(true)
  }

  const handleBannerRemove = () => {
    setBannerFile([])
    setRemoveBanner(true)
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    message.success('Copied to clipboard')
  }

  const handleCreateTestRegistrant = async () => {
    setCreatingTestRegistrant(true)
    try {
      const result = await api.post<TestRegistrantResult>(
        `/events/${event.slug}/settings/test_registrant`
      )
      setTestRegistrant(result)
      message.success(result.created ? 'Test attendee created' : 'You are already registered for this event')
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to create test attendee')
    } finally {
      setCreatingTestRegistrant(false)
    }
  }

  const handleRotatePreviewLink = () => {
    setRotatingPreview(true)
    router.post(
      `/events/${event.slug}/settings/rotate_preview_token`,
      {},
      {
        preserveScroll: true,
        onSuccess: () => message.success('Preview link regenerated'),
        onError: () => message.error('Failed to regenerate preview link'),
        onFinish: () => setRotatingPreview(false),
      }
    )
  }

  const openQrModal = (type: 'attendee_app' | 'registration_form') => {
    setQrModalType(type)
    setQrModalOpen(true)
  }

  const downloadQrCard = async (type: 'attendee_app' | 'registration_form') => {
    const ref = type === 'attendee_app' ? attendeeAppQrRef : registrationFormQrRef
    const isReady = type === 'attendee_app' ? attendeeAppQrReady : registrationFormQrReady

    if (!ref.current) return

    // Show loading message
    const hideLoading = message.loading('Generating QR code...', 0)

    try {
      // Wait for QR card to be ready if not already
      if (!isReady) {
        await new Promise(resolve => setTimeout(resolve, 1500))
      }

      const canvas = await html2canvas(ref.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
      })

      const link = document.createElement('a')
      const filename = type === 'attendee_app'
        ? `${event.slug}-attendee-app-qr.png`
        : `${event.slug}-registration-form-qr.png`
      link.download = filename
      link.href = canvas.toDataURL('image/png')
      link.click()

      hideLoading()
      message.success('QR code downloaded')
    } catch (error) {
      hideLoading()
      console.error('Failed to download QR card:', error)
      message.error('Failed to download QR code')
    }
  }

  const handleSave = (newStatus?: 'live' | 'draft') => {
    setSaving(true)

    const formDataObj = new FormData()
    formDataObj.append('event[name]', formData.name)
    formDataObj.append('event[slug]', formData.slug)
    formDataObj.append('event[start_date]', formData.start_date)
    formDataObj.append('event[end_date]', formData.end_date)
    formDataObj.append('event[start_time]', formData.start_time ?? '')
    formDataObj.append('event[end_time]', formData.end_time ?? '')
    formDataObj.append('event[location]', formData.location)
    formDataObj.append('event[description]', formData.description)
    formDataObj.append('event[theme]', formData.theme)
    formDataObj.append('event[client_name]', formData.client_name)
    formDataObj.append('event[allow_public_registration]', String(formData.allow_public_registration))
    formDataObj.append('event[allow_attendee_app_access]', String(formData.allow_attendee_app_access))
    formDataObj.append('event[allow_whatsapp_login]', String(formData.allow_whatsapp_login))
    formDataObj.append('event[auto_approve_registrations]', String(formData.auto_approve_registrations))
    formDataObj.append('event[show_event_details]', String(formData.show_event_details))

    formData.category_ids.forEach((id) => {
      formDataObj.append('event[category_ids][]', String(id))
    })

    if (newStatus) {
      formDataObj.append('event[status]', newStatus)
    }

    // Handle logo
    if (removeLogo) {
      formDataObj.append('event[remove_logo]', 'true')
    } else if (logoFile.length > 0 && logoFile[0].originFileObj) {
      formDataObj.append('event[logo]', logoFile[0].originFileObj)
    }

    // Handle banner
    if (removeBanner) {
      formDataObj.append('event[remove_banner]', 'true')
    } else if (bannerFile.length > 0 && bannerFile[0].originFileObj) {
      formDataObj.append('event[banner]', bannerFile[0].originFileObj)
    }

    router.post(`/events/${event.slug}/settings`, formDataObj, {
      forceFormData: true,
      onSuccess: (page) => {
        setSaving(false)
        if (newStatus === 'draft') {
          setRevertedMessage(true)
          setTimeout(() => setRevertedMessage(false), 3000)
        }
        const pageProps = (page as { props?: { event?: { slug?: string } } })?.props
        const updatedSlug = pageProps?.event?.slug || normalizedSlug
        const destination = updatedSlug ? `/events/${updatedSlug}/settings` : null
        if (destination && window.location.pathname !== destination) {
          router.visit(destination, { replace: true })
        }
      },
      onError: () => {
        setSaving(false)
        message.error('Failed to save settings')
      },
    })
  }

  const handlePublish = () => {
    handleSave('live')
  }

  const handleRevertToDraft = () => {
    handleSave('draft')
  }

  return (
    <EventLayout>
      <PageHeader
        title="Event Settings"
        subtitle="Edit event settings such as name and dates."
      />

      <ContentSection padding="lg">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Form Fields */}
          <div className="space-y-6">
            {/* Event Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Event Name</label>
              <Input
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Enter event name"
              />
            </div>

            {/* Event Dates */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Event Dates</label>
              <RangePicker
                value={[dayjs(formData.start_date), dayjs(formData.end_date)]}
                onChange={handleDateRangeChange}
                format="YYYY-MM-DD"
                style={{ width: '100%' }}
                size="small"
              />
            </div>

            {/* Event Times */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Event Times</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <TimePicker
                  value={formData.start_time ? dayjs(formData.start_time, 'HH:mm') : null}
                  onChange={handleStartTimeChange}
                  placeholder="Start time"
                  format="h:mmA"
                  allowClear
                  style={{ width: '100%' }}
                  size="small"
                />
                <TimePicker
                  value={formData.end_time ? dayjs(formData.end_time, 'HH:mm') : null}
                  onChange={handleEndTimeChange}
                  placeholder="End time (optional)"
                  format="h:mmA"
                  allowClear
                  disabled={!formData.start_time}
                  style={{ width: '100%' }}
                  size="small"
                />
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
              <Input
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                placeholder="Enter event location"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Event Description
              </label>
              <TextArea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Enter event description"
                rows={4}
              />
              <div className="mt-4">
                <Switch
                  checked={formData.show_event_details}
                  onChange={(checked) => handleInputChange('show_event_details', checked)}
                  label="Show event details on attendee app"
                />
              </div>
            </div>

            {/* Theme Colour */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Theme Colour</label>
              <ThemeColorButton
                value={formData.theme}
                onClick={() => setThemeDrawerOpen(true)}
                colors={DEFAULT_THEME_COLORS}
              />
            </div>

            {/* Event Logo */}
            <ImageUpload
              label="Event Logo"
              fileList={logoFile}
              onChange={handleLogoChange}
              onRemove={handleLogoRemove}
              helpText="Maximum upload file size: 2 MB"
            />

            {/* Event Banner */}
            <ImageUpload
              label="Event Banner"
              fileList={bannerFile}
              onChange={handleBannerChange}
              onRemove={handleBannerRemove}
              helpText="Maximum upload file size: 2 MB"
            />

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <Select
                mode="multiple"
                value={formData.category_ids}
                onChange={(value) => handleInputChange('category_ids', value)}
                options={categories.map((cat) => ({ value: cat.id, label: cat.name }))}
                placeholder="Select categories"
                style={{ width: '100%' }}
                size="small"
              />
            </div>

            {/* Client Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Client Name</label>
              <Input
                value={formData.client_name}
                onChange={(e) => handleInputChange('client_name', e.target.value)}
                placeholder="Enter client name"
              />
            </div>

            {/* Toggles */}
            <div className="space-y-4 pt-4">
              <Switch
                checked={formData.allow_public_registration}
                onChange={(checked) => handleInputChange('allow_public_registration', checked)}
                label="Allow Public Registration"
              />
              <div>
                <Switch
                  checked={formData.allow_attendee_app_access}
                  onChange={(checked) => handleInputChange('allow_attendee_app_access', checked)}
                  label="Allow Attendee App Access"
                />
                {!formData.allow_attendee_app_access && (
                  <p className="mt-1 ml-[52px] text-xs text-gray-500">
                    Use the Private Preview Link to test the attendee app before switching this on.
                  </p>
                )}
              </div>
              <div>
                <Switch
                  checked={formData.allow_whatsapp_login}
                  onChange={(checked) => handleInputChange('allow_whatsapp_login', checked)}
                  label="Allow WhatsApp OTP Login"
                />
                <p className="mt-1 ml-[52px] text-xs text-gray-500">
                  Attendees can log in with the phone number they registered with (country code required).
                  Sends a Meta authentication message per login request, billed per message.
                </p>
              </div>
              <Switch
                checked={formData.auto_approve_registrations}
                onChange={(checked) => handleInputChange('auto_approve_registrations', checked)}
                label="Auto-approve Registrations"
              />
            </div>
          </div>

          {/* Right Column - Public Links */}
          <div>
            <Card title="Public Links">
              <div className="space-y-4">
                {/* Event Slug */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Event Slug</label>
                  <Input
                    value={formData.slug}
                    onChange={(e) => handleInputChange('slug', e.target.value)}
                    placeholder="Enter event slug"
                  />
                  <div className="mt-2 flex items-start gap-2 text-xs text-rose-700">
                    <ExclamationCircleOutlined className="mt-[2px]" />
                    <span>
                      Changing the slug updates all public URLs and QR codes. Old links will stop working.
                    </span>
                  </div>
                  {slugChanged && (
                    <div className="mt-2 text-xs text-amber-700">
                      New slug will be saved as: <span className="font-medium">{normalizedSlug || '(empty)'}</span>
                    </div>
                  )}
                </div>

                {/* Preview URL (Attendee App) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Attendee App Link
                  </label>
                  <div className="flex items-center gap-2">
                    <div
                      className="flex-1 flex items-center gap-2.5 px-3 py-2 bg-[#F3F5F8] border border-[#EBEBEB] cursor-pointer hover:bg-[#E8EAED] transition-colors"
                      onClick={() => copyToClipboard(preview_urls.attendee_app)}
                    >
                      <span className="flex-1 text-sm text-black/85 break-all">
                        {preview_urls.attendee_app}
                      </span>
                      <CopyOutlined className="text-black/45 flex-shrink-0" />
                    </div>
                    <Tooltip title="View QR Code">
                      <button
                        type="button"
                        onClick={() => openQrModal('attendee_app')}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                      >
                        <QrcodeOutlined className="text-lg" />
                      </button>
                    </Tooltip>
                    <Tooltip title="Download QR Code">
                      <button
                        type="button"
                        onClick={() => downloadQrCard('attendee_app')}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                      >
                        <DownloadOutlined className="text-lg" />
                      </button>
                    </Tooltip>
                  </div>
                </div>

                {/* Private Preview Link (works while attendee app access is off) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Private Preview Link
                  </label>
                  <div className="flex items-center gap-2">
                    <div
                      className="flex-1 flex items-center gap-2.5 px-3 py-2 bg-[#F3F5F8] border border-[#EBEBEB] cursor-pointer hover:bg-[#E8EAED] transition-colors"
                      onClick={() => copyToClipboard(preview_urls.attendee_app_preview)}
                    >
                      <span className="flex-1 text-sm text-black/85 break-all">
                        {preview_urls.attendee_app_preview}
                      </span>
                      <CopyOutlined className="text-black/45 flex-shrink-0" />
                    </div>
                    <Tooltip title="Regenerate link — the old preview link stops working">
                      <button
                        type="button"
                        onClick={handleRotatePreviewLink}
                        disabled={rotatingPreview}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
                      >
                        <ReloadOutlined className={`text-lg ${rotatingPreview ? 'animate-spin' : ''}`} />
                      </button>
                    </Tooltip>
                  </div>
                  <div className="mt-2 flex items-start gap-2 text-xs text-amber-700">
                    <ExclamationCircleOutlined className="mt-[2px]" />
                    <span>
                      Opens the attendee app for you while Allow Attendee App Access stays off. You still
                      log in with a registrant email and OTP. Keep it private — anyone holding this link
                      reaches the login screen.
                    </span>
                  </div>

                  <Button
                    variant="secondary"
                    icon={<UserAddOutlined />}
                    onClick={handleCreateTestRegistrant}
                    loading={creatingTestRegistrant}
                    className="mt-3"
                  >
                    Create test attendee
                  </Button>

                  {testRegistrant && (
                    <div className="mt-3 px-3 py-2.5 bg-[#F6FFED] border border-[#B7EB8F] text-xs text-black/85">
                      <div className="font-medium mb-1.5">
                        {testRegistrant.created
                          ? 'Test attendee created — log in with:'
                          : 'You are already registered for this event — log in with:'}
                      </div>
                      <div
                        className="flex items-center gap-2 cursor-pointer"
                        onClick={() => copyToClipboard(testRegistrant.email)}
                      >
                        <span className="break-all">{testRegistrant.email}</span>
                        <CopyOutlined className="text-black/45 flex-shrink-0" />
                      </div>
                      <div
                        className="flex items-center gap-2 mt-1 cursor-pointer"
                        onClick={() => copyToClipboard(testRegistrant.otp)}
                      >
                        <span>
                          OTP <span className="font-mono font-medium tracking-wider">{testRegistrant.otp}</span>
                        </span>
                        <CopyOutlined className="text-black/45 flex-shrink-0" />
                      </div>
                      <div className="mt-1.5 text-black/45">
                        This OTP does not expire — it is this attendee&apos;s permanent login code. Open the
                        preview link above, choose Login, and enter it.
                      </div>
                      {testRegistrant.incomplete_answers && (
                        <div className="mt-1.5 text-amber-700">
                          Some required registration fields could not be auto-filled (e.g. file uploads).
                          Complete them from the Attendees page if the app needs them.
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Preview URL (Registration Form) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Registration Form Link
                  </label>
                  <div className="flex items-center gap-2">
                    <div
                      className="flex-1 flex items-center gap-2.5 px-3 py-2 bg-[#F3F5F8] border border-[#EBEBEB] cursor-pointer hover:bg-[#E8EAED] transition-colors"
                      onClick={() => copyToClipboard(preview_urls.registration_form)}
                    >
                      <span className="flex-1 text-sm text-black/85 break-all">
                        {preview_urls.registration_form}
                      </span>
                      <CopyOutlined className="text-black/45 flex-shrink-0" />
                    </div>
                    <Tooltip title="View QR Code">
                      <button
                        type="button"
                        onClick={() => openQrModal('registration_form')}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                      >
                        <QrcodeOutlined className="text-lg" />
                      </button>
                    </Tooltip>
                    <Tooltip title="Download QR Code">
                      <button
                        type="button"
                        onClick={() => downloadQrCard('registration_form')}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                      >
                        <DownloadOutlined className="text-lg" />
                      </button>
                    </Tooltip>
                  </div>
                </div>
              </div>
            </Card>

            {/* Reverted Message */}
            {revertedMessage && (
              <div className="mt-4 flex items-center gap-2 text-green-600 bg-green-50 px-4 py-2 rounded">
                <CheckOutlined />
                <span>You have reverted to Draft.</span>
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center gap-3 mt-8 pt-6 border-t border-gray-200">
          {event.status === 'live' ? (
            <>
              <Button
                variant="primary"
                icon={<CheckOutlined />}
                onClick={() => handleSave()}
                loading={saving}
                className="bg-green-600 hover:bg-green-700"
              >
                Published Event (Live)
              </Button>
              <Button variant="secondary" onClick={handleRevertToDraft} loading={saving}>
                Revert to Draft
              </Button>
            </>
          ) : (
            <>
              <Button variant="primary" onClick={handlePublish} loading={saving}>
                Publish Event (Go Live)
              </Button>
              <Button
                variant="secondary"
                onClick={() => handleSave()}
                loading={saving}
                icon={<CheckOutlined />}
              >
                Save as Draft
              </Button>
            </>
          )}
        </div>
      </ContentSection>

      {/* Theme Colour Picker */}
      <ThemeColorPicker
        value={formData.theme}
        onChange={(value) => handleInputChange('theme', value)}
        colors={DEFAULT_THEME_COLORS}
        open={themeDrawerOpen}
        onClose={() => setThemeDrawerOpen(false)}
      />

      {/* QR Preview Modal */}
      <QRPreviewModal
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        title={event.name}
        url={qrModalType === 'attendee_app' ? preview_urls.attendee_app : preview_urls.registration_form}
        subtitle={qrModalType === 'attendee_app' ? 'Attendee App' : 'Registration Form'}
        downloadFilename={`${event.slug}-${qrModalType === 'attendee_app' ? 'attendee-app' : 'registration-form'}-qr`}
      />

      {/* Hidden QR Cards for direct download */}
      <div className="fixed -left-[9999px] top-0" aria-hidden="true">
        <QRCard
          ref={attendeeAppQrRef}
          title={event.name}
          url={preview_urls.attendee_app}
          subtitle="Attendee App"
          onReady={() => setAttendeeAppQrReady(true)}
        />
        <QRCard
          ref={registrationFormQrRef}
          title={event.name}
          url={preview_urls.registration_form}
          subtitle="Registration Form"
          onReady={() => setRegistrationFormQrReady(true)}
        />
      </div>
    </EventLayout>
  )
}
